import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { tareasApi } from "src/services/tareasApi";
import { dbx } from "src/utils/urlBack_End";

export const usePortafolioTarea = (tareaId) => {
  const { empleado: user } = useSelector((state) => state.auth.user);
  const [deletingFile, setDeletingFile] = useState(false);

  // RTK Query para obtener portafolios
  const {
    data: portafoliosData,
    isLoading,
    error,
    refetch,
  } = tareasApi.useObtenerPortafoliosTareaQuery(
    { idTarea: tareaId },
    { skip: !tareaId },
  );

  // Mutation para eliminar archivos
  const [eliminarArchivoMutation] =
    tareasApi.useEliminarArchivoPortafolioTareaMutation();

  // Mutation para eliminar una bitácora
  const [eliminarPortafolioMutation] =
    tareasApi.useEliminarPortafolioTareaMutation();

  // Crear carpeta en Dropbox si no existe
  const createDropboxFolder = useCallback(async () => {
    if (!tareaId) return;

    try {
      await dbx.filesListFolder({ path: `/Tareas/${tareaId}/Portafolio` });
    } catch {
      try {
        await dbx.filesCreateFolderV2({
          path: `/Tareas/${tareaId}/Portafolio`,
        });
      } catch (error) {
        console.error("Error creando carpeta en Dropbox:", error);
      }
    }
  }, [tareaId]);

  // Eliminar archivo
  const deleteFile = useCallback(
    async (archivo) => {
      setDeletingFile(true);
      if (!archivo || !tareaId) {
        return { success: false, error: "Datos incompletos" };
      }

      try {
        // Eliminar de Dropbox
        await dbx.filesDeleteV2({
          path: `/Tareas/${tareaId}/Portafolio/${archivo.nombreArchivo}`,
        });

        // Eliminar de BD usando RTK Query
        await eliminarArchivoMutation({
          id: archivo.id,
          refPortafolioTarea: archivo.refPortafolioTarea,
          empleadoIngresaPortafolio: `${user.nombre} ${user.apellidos}`,
        }).unwrap();

        return { success: true };
      } catch (error) {
        console.error("Error eliminando archivo:", error);
        return {
          success: false,
          error: error.data?.message || "Error al eliminar el archivo",
        };
      } finally {
        setDeletingFile(false);
      }
    },
    [tareaId, user.nombre, user.apellidos, eliminarArchivoMutation],
  );

  const deletePortafolio = useCallback(
    async (portafolio) => {
      if (!portafolio || !tareaId) {
        return { success: false, error: "Datos incompletos" };
      }

      try {
        // Primero eliminar todos los archivos de Dropbox
        if (portafolio.archivosPortafolio?.length > 0) {
          for (const archivo of portafolio.archivosPortafolio) {
            try {
              await dbx.filesDeleteV2({
                path: `/Tareas/${tareaId}/Portafolio/${archivo.nombreArchivo}`,
              });
            } catch (error) {
              console.warn(
                `No se pudo eliminar archivo ${archivo.nombreArchivo}:`,
                error,
              );
            }
          }
        }

        // Eliminar portafolio de BD
        await eliminarPortafolioMutation(portafolio.id).unwrap();

        return { success: true };
      } catch (error) {
        console.error("Error eliminando portafolio:", error);
        return {
          success: false,
          error: error.data?.message || "Error al eliminar la entrada",
        };
      }
    },
    [tareaId, eliminarPortafolioMutation],
  );

  // Procesar datos para el render
  const processedPortafolios = useMemo(() => {
    if (!portafoliosData || !Array.isArray(portafoliosData)) {
      return [];
    }

    return portafoliosData
      .map((portafolio) => {
        const historialPortafolios = portafolio.historialPortafolios || [];
        const archivos = portafolio.archivos || [];

        // Obtener última modificación
        const ultimaModificacion = historialPortafolios
          .filter((element) => element.refPortafolioTarea === portafolio.id)
          .sort((a, b) => new Date(b.hora) - new Date(a.hora))[0];

        // Filtrar archivos del portafolio
        const archivosPortafolio = archivos.filter(
          (element) => element.refPortafolioTarea === portafolio.id,
        );

        return {
          ...portafolio,
          ultimaModificacion,
          archivosPortafolio,
        };
      })
      .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
  }, [portafoliosData]);

  useEffect(() => {
    if (tareaId) {
      createDropboxFolder();
    }
  }, [tareaId, createDropboxFolder]);

  return {
    portafolios: processedPortafolios,
    loading: isLoading,
    deletingFile,
    error:
      error?.data?.message || (error ? "Error al cargar portafolios" : null),
    refreshPortafolios: refetch,
    deleteFile,
    deletePortafolio,
  };
};
